import { SensorData } from '@gozoki/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Card, CardContent, Chip, Typography } from '@mui/material';

interface SensorStatusCardsProps {
    sensors: SensorData[] | undefined;
}

const SensorStatusCards = ({ sensors }: SensorStatusCardsProps) => {
    if (!sensors) {
        return null;
    }

    // Calculate statistics
    const sensorsWithReadings = sensors.filter((s) => s.latestReading !== null);
    const sensorsOk = sensorsWithReadings.filter((s) => {
        if (!s.latestReading) return false;
        const temp = s.latestReading.temperature;
        // Assuming threshold is 0-6°C (this should match your backend logic)
        return temp >= 0 && temp <= 6;
    }).length;

    const activeAlerts = sensorsWithReadings.filter((s) => {
        if (!s.latestReading) return false;
        const temp = s.latestReading.temperature;
        // Alert if temperature is outside 0-6°C range
        return temp < 0 || temp > 6;
    }).length;

    // Determine global status
    let globalStatus: 'normal' | 'alert' | 'warning' = 'normal';
    if (activeAlerts > 0) {
        globalStatus = 'alert';
    } else if (sensorsOk === sensorsWithReadings.length) {
        globalStatus = 'normal';
    } else {
        globalStatus = 'warning';
    }

    let statusText = 'Normal';
    let statusColor = '#2e7d32';
    let statusBgColor = '#e8f5e9';
    if (globalStatus === 'alert') {
        statusText = 'Alerte';
        statusColor = '#d32f2f';
        statusBgColor = '#ffebee';
    } else if (globalStatus === 'warning') {
        statusText = 'Attention';
        statusColor = '#ff9800';
        statusBgColor = '#fff3e0';
    }

    // Compute alert label
    let alertLabel = 'Aucune alerte';
    if (activeAlerts === 1) {
        alertLabel = '1 alerte';
    } else if (activeAlerts > 1) {
        alertLabel = `${activeAlerts} alertes`;
    }

    const iconSize = 40;

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                marginTop: '16px',
                marginBottom: '16px',
            }}
        >
            {/* Global Status Card */}
            <Card
                sx={{
                    flex: 1,
                    backgroundColor: statusBgColor,
                    borderRadius: 2,
                }}
            >
                <CardContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box>
                            <Typography
                                variant="body2"
                                color={statusColor}
                                sx={{ marginBottom: 1 }}
                            >
                                État Global
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'black',
                                    marginBottom: 1,
                                }}
                            >
                                {statusText}
                            </Typography>
                            <Chip
                                label={`${sensorsOk}/${sensorsWithReadings.length} équipements OK`}
                                size="small"
                                sx={{
                                    backgroundColor: statusColor,
                                    color: 'white',
                                    fontSize: '0.75rem',
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '100%',
                                // backgroundColor: `${statusColor}`, // 33 = 20% opacity in hex
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {globalStatus === 'alert' ? (
                                <WarningAmberRoundedIcon
                                    sx={{
                                        fontSize: iconSize,
                                        color: statusColor,
                                    }}
                                />
                            ) : (
                                <CheckCircleIcon
                                    sx={{
                                        fontSize: iconSize,
                                        color: statusColor,
                                    }}
                                />
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Active Alerts Card */}
            <Card
                sx={{
                    flex: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 2,
                }}
            >
                <CardContent>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <Box>
                            <Typography
                                variant="body2"
                                sx={{
                                    marginBottom: 1,
                                    color: activeAlerts > 0 ? '#d32f2f' : '#666',
                                }}
                            >
                                Alertes Actives
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 'bold',
                                    color: 'black',
                                    marginBottom: 1,
                                }}
                            >
                                {activeAlerts}
                            </Typography>
                            <Chip
                                label={alertLabel}
                                size="small"
                                sx={{
                                    backgroundColor: activeAlerts > 0 ? '#d32f2f' : '#9e9e9e',
                                    color: 'white',
                                    fontSize: '0.75rem',
                                }}
                            />
                        </Box>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default SensorStatusCards;
